import { canEditRules, classifyRulesSaveError, rulesScopeOf, maxBytesFromError, rulesSaveMessage, type RulesSaveFailure } from './ids';
import type { RulesDocument, RulesScope } from './types';

export interface RulesSaveDeps {
  orgId: string;
  scope: RulesScope;
  content: string;
  /** Version the tab was loaded from / last saved as. */
  baseVersion: number | undefined;
  save: (orgId: string, scope: RulesScope, content: string, baseVersion: number) => Promise<RulesDocument>;
  onSaved: (doc: RulesDocument) => void;
  onFailed: (kind: RulesSaveFailure, message: string) => void;
}

/**
 * The rules save path (bypasses the branch flow entirely). Success hands the new document
 * back so the caller can reset dirty state and the base version; failure is classified by
 * HTTP status (409 conflict, 413 too large, 403 forbidden) and reported once.
 */
export async function runRulesSave(deps: RulesSaveDeps): Promise<'saved' | RulesSaveFailure> {
  if (!deps.orgId || deps.baseVersion === undefined) {
    deps.onFailed('error', 'Rules are still loading — try again in a moment.');
    return 'error';
  }
  try {
    const doc = await deps.save(deps.orgId, deps.scope, deps.content, deps.baseVersion);
    deps.onSaved(doc);
    return 'saved';
  } catch (error) {
    const kind = classifyRulesSaveError(error);
    deps.onFailed(kind, rulesSaveMessage(kind, deps.scope, maxBytesFromError(error)));
    return kind;
  }
}

export interface SaveDispatch {
  /** Rules tab the caller may edit: save to the rules store. */
  rules: (scope: RulesScope) => void;
  /** Any other tab: the branch save. Never called for a rules tab. */
  branch: () => void;
}

/**
 * The one routing decision for Save. A rules tab goes to the rules store or is refused
 * (read-only ORG_RULES.md for a non-admin); it can never reach the branch save.
 */
export function dispatchSave(
  fileId: string,
  role: 'OWNER' | 'ADMIN' | 'MEMBER' | null | undefined,
  handlers: SaveDispatch,
): 'rules' | 'branch' | 'refused' {
  const scope = rulesScopeOf(fileId);
  if (!scope) {
    handlers.branch();
    return 'branch';
  }
  if (!canEditRules(scope, role)) return 'refused';
  handlers.rules(scope);
  return 'rules';
}

/**
 * Whether Save may start. A tab stays dirty while its save is in flight (edits typed meanwhile
 * are kept), so without the `isSaving` guard a second Ctrl+S would resend the old base_version
 * and get a false 409.
 */
export function canStartSave(tab: { isDirty: boolean; isSaving: boolean; viewingMain: boolean }): boolean {
  return tab.isDirty && !tab.isSaving && !tab.viewingMain;
}
